'use client';

import type { FormEvent } from 'react';
import { useMemo, useState } from 'react';
import ActionIconButton from '@/components/ActionIconButton';
import AppFooter from '@/components/AppFooter';
import AppLogo from '@/components/AppLogo';
import BottomNavigation from '@/components/BottomNavigation';
import { CATEGORY_ICONS, CategoryIcon } from '@/components/CategoryIcon';
import ColorPicker from '@/components/ColorPicker';
import { useAppSettings } from '@/hooks/useAppSettings';
import { useCategories } from '@/hooks/useCategories';
import { CATEGORY_COLORS, colorForIndex } from '@/lib/colors';
import { formatMoney } from '@/lib/money';

export default function BudgetsPage() {
  const { categories, addCategory, updateCategory, deleteCategory, loading, error } = useCategories();
  const { settings } = useAppSettings();
  const [name, setName] = useState('');
  const [budget, setBudget] = useState('');
  const [color, setColor] = useState(colorForIndex(0));
  const [colorPanelOpen, setColorPanelOpen] = useState(false);
  const [icon, setIcon] = useState('wallet');
  const [activeId, setActiveId] = useState<string | null>(null);
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);

  const activeCategory = useMemo(
    () => categories.find((category) => category.id === activeId) ?? null,
    [categories, activeId]
  );
  const pendingDeleteCategory = useMemo(
    () => categories.find((category) => category.id === pendingDeleteId) ?? null,
    [categories, pendingDeleteId]
  );
  const totalMonthlyBudget = categories.reduce((sum, category) => sum + (category.monthlyBudget ?? 0), 0);
  const categoriesWithBudget = categories.filter((category) => typeof category.monthlyBudget === 'number').length;
  const money = (value: number) => formatMoney(value, settings.currency, 0);

  const chooseColor = (nextColor: string) => {
    setColor(nextColor);
  };

  const resetForm = () => {
    setActiveId(null);
    setName('');
    setBudget('');
    chooseColor(colorForIndex(categories.length));
    setIcon('wallet');
    setColorPanelOpen(false);
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const monthlyBudget = budget.trim() === '' ? undefined : Number(budget);
    if (!name.trim() || (budget.trim() !== '' && isNaN(Number(budget)))) return;

    const payload = { name: name.trim(), monthlyBudget, color, icon };

    if (activeCategory) {
      await updateCategory(activeCategory.id, payload);
    } else {
      await addCategory(payload);
    }
    resetForm();
  };

  const startEdit = (id: string) => {
    const category = categories.find((item) => item.id === id);
    if (!category) return;
    const categoryIndex = Math.max(categories.findIndex((item) => item.id === id), 0);

    setActiveId(id);
    setName(category.name);
    setBudget(category.monthlyBudget !== undefined ? String(category.monthlyBudget) : '');
    chooseColor(category.color ?? colorForIndex(categoryIndex));
    setIcon(category.icon ?? 'wallet');
    setColorPanelOpen(false);
  };

  const confirmDelete = async () => {
    if (!pendingDeleteId) return;
    const id = pendingDeleteId;

    if (activeId === id) {
      resetForm();
    }
    setPendingDeleteId(null);
    await deleteCategory(id);
  };

  return (
    <main className="page-shell">
      <div className="page-inner">
        <section className="overflow-hidden rounded-2xl border border-brand-500/20 bg-slate-900 shadow-sm">
          <div className="bg-brand-700 px-4 py-5 text-white min-[380px]:px-5">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
              <div className="min-w-0">
                <AppLogo className="text-brand-100" markClassName="border-white/25" textClassName="text-brand-100" />
                <h1 className="mt-1 text-xl font-semibold leading-tight text-white min-[380px]:text-2xl">
                  Catégories et limites
                </h1>
                <p className="mt-2 text-sm leading-6 text-brand-100">
                  Fixez quelques repères simples, sans rendre le suivi pénible.
                </p>
              </div>
              <div className="rounded-2xl bg-slate-950/45 p-3 sm:w-48">
                <p className="text-xs font-medium text-brand-100">Budget mensuel</p>
                <p className="mt-1 truncate text-lg font-semibold text-brand-100">{money(totalMonthlyBudget)}</p>
              </div>
            </div>
          </div>

          <div className="grid gap-px bg-slate-800 sm:grid-cols-3">
            <div className="bg-slate-900 p-4">
              <p className="text-sm font-medium text-slate-400">Catégories</p>
              <p className="mt-2 text-2xl font-semibold text-slate-100">{categories.length}</p>
            </div>
            <div className="bg-slate-900 p-4">
              <p className="text-sm font-medium text-slate-400">Avec limite</p>
              <p className="mt-2 text-2xl font-semibold text-brand-200">{categoriesWithBudget}</p>
            </div>
            <div className="bg-slate-900 p-4">
              <p className="text-sm font-medium text-slate-400">Sans limite</p>
              <p className="mt-2 text-2xl font-semibold text-slate-100">{categories.length - categoriesWithBudget}</p>
            </div>
          </div>
        </section>

        <section className="section-card">
          <div className="mb-5 flex flex-col gap-3 min-[420px]:flex-row min-[420px]:items-start min-[420px]:justify-between">
            <div className="min-w-0">
              <h2 className="text-lg font-semibold text-slate-100">
                {activeCategory ? 'Modifier la catégorie' : 'Nouvelle catégorie'}
              </h2>
              <p className="mt-2 text-sm text-slate-400">
                Nom, limite mensuelle, couleur et icône. Le reste peut attendre.
              </p>
            </div>
            {activeCategory ? (
              <span className="w-fit rounded-full bg-brand-500/15 px-3 py-1 text-xs font-semibold text-brand-200">
                En édition
              </span>
            ) : null}
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <label className="block">
                <span className="text-sm font-medium text-slate-200">Nom de la catégorie</span>
                <input
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  placeholder="Courses, loisirs..."
                  className="mt-2 w-full px-4 py-3"
                />
              </label>
              <label className="block">
                <span className="text-sm font-medium text-slate-200">Budget mensuel</span>
                <input
                  value={budget}
                  onChange={(event) => setBudget(event.target.value)}
                  placeholder="400"
                  type="number"
                  className="mt-2 w-full px-4 py-3"
                />
              </label>
            </div>

            <div className="rounded-2xl border border-slate-800 bg-slate-950 p-3 min-[380px]:p-4">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-sm font-semibold text-slate-200">Couleur</p>
                  <p className="mt-1 text-xs text-slate-500">Visible dans les cartes et graphiques.</p>
                </div>
                <span className="h-10 w-10 shrink-0 rounded-full border border-slate-700" style={{ backgroundColor: color }} />
              </div>

              <div className="mt-4 grid grid-cols-5 gap-2 min-[420px]:grid-cols-7">
                {CATEGORY_COLORS.map((item) => (
                  <button
                    key={item}
                    type="button"
                    onClick={() => chooseColor(item)}
                    className={`h-11 w-full rounded-2xl border-2 shadow-none ${
                      color === item ? 'border-slate-100' : 'border-slate-700'
                    }`}
                    style={{ backgroundColor: item }}
                    aria-label={`Choisir la couleur ${item}`}
                  />
                ))}
                <button
                  type="button"
                  onClick={() => setColorPanelOpen((current) => !current)}
                  className={`relative h-11 w-full overflow-hidden rounded-2xl border-2 bg-slate-900 shadow-none ${
                    CATEGORY_COLORS.includes(color) ? 'border-slate-700' : 'border-slate-100'
                  }`}
                  title="Couleur personnalisée"
                  aria-label="Choisir une couleur personnalisée"
                  aria-expanded={colorPanelOpen}
                >
                  <span className="absolute inset-2 rounded-xl" style={{ backgroundColor: color }} />
                </button>
              </div>

              {colorPanelOpen ? (
                <div className="mt-4 rounded-2xl border border-slate-800 bg-slate-900 p-3">
                  <ColorPicker value={color} onChange={chooseColor} />
                </div>
              ) : null}
            </div>

            <div>
              <span className="text-sm font-medium text-slate-200">Icône</span>
              <div className="mt-2 grid grid-cols-4 gap-2 min-[420px]:grid-cols-5 sm:grid-cols-6">
                {CATEGORY_ICONS.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => setIcon(item.id)}
                    className={`flex min-h-14 items-center justify-center rounded-2xl border px-3 py-3 shadow-none ${
                      icon === item.id ? 'border-brand-400 bg-brand-500/15' : 'border-slate-800 bg-slate-950'
                    }`}
                    title={item.label}
                    aria-label={item.label}
                    aria-pressed={icon === item.id}
                  >
                    <CategoryIcon icon={item.id} color={color} className="h-8 w-8" />
                  </button>
                ))}
              </div>
            </div>

            <div className="flex flex-col gap-3 sm:flex-row">
              <button type="submit" className="primary-button sm:flex-1">
                {activeCategory ? 'Enregistrer' : 'Ajouter la catégorie'}
              </button>
              {activeCategory ? (
                <button type="button" onClick={resetForm} className="secondary-button sm:flex-1">
                  Annuler
                </button>
              ) : null}
            </div>
          </form>
        </section>

        <section className="section-card">
          <div className="mb-4 flex items-center justify-between gap-3">
            <div className="min-w-0">
              <h2 className="text-lg font-semibold text-slate-100">Mes catégories</h2>
              <p className="mt-2 text-sm text-slate-400">Ajustez les limites quand vos habitudes changent.</p>
            </div>
            <span className="shrink-0 rounded-full bg-slate-800 px-3 py-1 text-xs font-semibold text-slate-300">
              {categories.length}
            </span>
          </div>

          <div className="space-y-3">
            {loading ? (
              <div className="card-muted">Chargement...</div>
            ) : error ? (
              <div className="rounded-2xl border border-rose-900/70 bg-rose-950 p-4 text-sm text-rose-200">{error}</div>
            ) : categories.length === 0 ? (
              <div className="card-muted text-sm">Aucune catégorie pour l'instant.</div>
            ) : (
              categories.map((category) => (
                <div key={category.id} className="rounded-2xl border border-slate-800 bg-slate-900 p-4 shadow-sm">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex min-w-0 items-center gap-3">
                      <CategoryIcon category={category} className="h-10 w-10" />
                      <div className="min-w-0">
                        <p className="truncate font-semibold text-slate-100">{category.name}</p>
                        <p className="mt-1 text-sm text-slate-400">
                          {category.monthlyBudget !== undefined ? `${money(category.monthlyBudget)} par mois` : 'Aucune limite'}
                        </p>
                      </div>
                    </div>
                    <div className="flex justify-end gap-2 sm:w-auto">
                      <ActionIconButton
                        label="Modifier la catégorie"
                        icon="edit"
                        onClick={() => startEdit(category.id)}
                      />
                      <ActionIconButton
                        label="Supprimer la catégorie"
                        icon="trash"
                        tone="danger"
                        onClick={() => setPendingDeleteId(category.id)}
                      />
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </section>
      </div>

      {pendingDeleteCategory ? (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-slate-950/75 px-4 py-6 sm:items-center">
          <div
            className="w-full max-w-sm rounded-2xl border border-slate-700 bg-slate-900 p-4 shadow-xl min-[380px]:p-5"
            role="dialog"
            aria-modal="true"
            aria-label="Supprimer la categorie"
          >
            <h2 className="text-lg font-semibold text-slate-100">Supprimer la catégorie ?</h2>
            <p className="mt-3 text-sm leading-6 text-slate-400">
              La catégorie "{pendingDeleteCategory.name}" sera supprimée. Les transactions liées seront conservées sans catégorie.
            </p>
            <div className="mt-5 flex flex-col gap-3 sm:flex-row">
              <button type="button" onClick={() => setPendingDeleteId(null)} className="secondary-button sm:flex-1">
                Annuler
              </button>
              <button type="button" onClick={confirmDelete} className="danger-button sm:flex-1">
                Supprimer
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <BottomNavigation active="/budgets" />
      <div className="page-inner mt-5">
        <AppFooter />
      </div>
    </main>
  );
}
